import { createContext, type ReactNode, useCallback, useContext, useState } from "react";

import translations from "./translations";
import { type Locale, resolve, type TFunction, type TranslationKey } from "./types";

const LOCALE_KEY = "de-vurige-stand-locale";

const getStoredLocale = (): Locale => {
  try {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (stored === "en" || stored === "fr" || stored === "vl") return stored;
  } catch {
    // ignore
  }
  return "en";
};

type I18nContextType = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: TFunction;
};

const I18nContext = createContext<I18nContextType>({
  locale: "en",
  setLocale: () => {},
  t: ((key: string) => key) as TFunction,
});

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(getStoredLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(LOCALE_KEY, l);
    } catch {
      // ignore
    }
  }, []);

  const t = useCallback(
    ((key: TranslationKey, ...args: unknown[]): string => {
      const val = resolve(translations[locale] as unknown as Record<string, unknown>, key);
      if (typeof val === "function") {
        return (val as (...a: unknown[]) => string)(...args);
      }
      return (val as string) ?? key;
    }) as TFunction,
    [locale],
  );

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
};

export const useTranslations = () => useContext(I18nContext);
